import { useState, type FormEvent } from 'react';
import {
  useRecurringInvoices,
  useCreateRecurringInvoice,
  useUpdateRecurringInvoice,
  useDeleteRecurringInvoice,
  type RecurringInvoiceLineItem,
} from '../api/queries/recurring-invoices';
import { RECUR_OPTIONS, labelForRrule } from '../api/queries/recurring-expenses';
import { useClients } from '../api/queries/clients';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { fmtDate } from '../utils/date';
import { DateInput } from '../components/DateInput';
import { MoneyInput } from '../components/MoneyInput';
import { IconAction } from '../components/IconAction';
import { SortableTh } from '../components/SortableTh';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import './RecurringInvoicesPage.css';

type RecurringSortKey = 'name' | 'client' | 'schedule' | 'next_due' | 'subtotal' | 'status';

const CADENCES = RECUR_OPTIONS.filter((o) => o.rrule !== null) as {
  value: string;
  label: string;
  rrule: string;
}[];

import { formatMoney } from '../utils/money';

function todayEpoch(): number {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
}

/**
 * Embedded panel for Invoices → "Repeating rules" (same pattern as Expenses).
 * Kept as a named export so the old /recurring-invoices route can redirect
 * without losing the UI body.
 */
export function RecurringInvoicesPanel(): JSX.Element {
  if (!can('invoice.read')) {
    return <p className="ri-status">You don&apos;t have permission to view recurring invoices.</p>;
  }
  return <RecurringInvoicesPanelInner />;
}

/** @deprecated Standalone page — use Invoices → Repeating rules. */
export function RecurringInvoicesPage(): JSX.Element {
  return (
    <section className="page-stack page-stack-wide">
      <div className="card ri-toolbar">
        <header className="ri-header">
          <h2>Recurring Invoices</h2>
        </header>
      </div>
      <RecurringInvoicesPanel />
    </section>
  );
}

function RecurringInvoicesPanelInner(): JSX.Element {
  const list = useRecurringInvoices();
  const create = useCreateRecurringInvoice();
  const update = useUpdateRecurringInvoice();
  const remove = useDeleteRecurringInvoice();
  const clientsQ = useClients();

  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState<number>(0);
  const [rrule, setRrule] = useState('FREQ=MONTHLY');
  const [startsAt, setStartsAt] = useState<number | null>(() => todayEpoch());
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [taxAmount, setTaxAmount] = useState('0');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<RecurringInvoiceLineItem[]>([
    { description: '', quantity: '1', unit_amount: '0' },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [createdNote, setCreatedNote] = useState<string | null>(null);

  const resetForm = () => {
    setName('');
    setClientId(0);
    setRrule('FREQ=MONTHLY');
    setStartsAt(todayEpoch());
    setEndsAt(null);
    setTaxAmount('0');
    setNotes('');
    setItems([{ description: '', quantity: '1', unit_amount: '0' }]);
    setError(null);
  };

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setCreatedNote(null);
    if (clientId <= 0) {
      setError('Pick a client.');
      return;
    }
    if (items.length === 0 || items.some((li) => !li.description.trim())) {
      setError('Each line item needs a description.');
      return;
    }
    if (startsAt === null) {
      setError('Pick a start date.');
      return;
    }
    if (endsAt !== null && endsAt < startsAt) {
      setError('End date must be on or after the start date.');
      return;
    }
    try {
      const res = await create.mutateAsync({
        name: name.trim(),
        client_id: clientId,
        rrule,
        starts_at: startsAt,
        ends_at: endsAt,
        line_items: items,
        tax_amount: taxAmount.trim() || '0',
        notes: notes.trim() || undefined,
      });
      const n = res.generated_count ?? 0;
      setShowNew(false);
      resetForm();
      if (n > 0) {
        setCreatedNote(
          n === 1
            ? 'Created 1 draft invoice for a past-due period.'
            : `Created ${n} draft invoices for past-due periods.`,
        );
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { details?: { rrule?: string }; message?: string } | null;
        setError(body?.details?.rrule ?? body?.message ?? 'Validation failed.');
      } else {
        setError('Could not create template.');
      }
    }
  };

  const clients = clientsQ.data?.clients ?? [];
  const rows = list.data?.recurring_invoice_templates ?? [];
  const sort = useClientSort(rows, (t, key: RecurringSortKey) => {
    switch (key) {
      case 'name': return t.name;
      case 'client': return t.client_company_name ?? t.client_name ?? `#${t.client_id}`;
      case 'schedule': return t.rrule;
      case 'next_due': return t.next_due_at;
      case 'subtotal':
        return t.line_items.reduce(
          (sum, li) => sum + Number(li.quantity) * Number(li.unit_amount),
          0,
        );
      case 'status': return t.is_active ? 1 : 0;
      default: return '';
    }
  }, { key: 'name', dir: 'asc' });

  return (
    <>
      {createdNote && (
        <div className="card">
          <p className="ri-created-note" role="status">{createdNote}</p>
        </div>
      )}

      {can('invoice.write') && (
        <div className="card">
          {!showNew ? (
            <div className="ri-panel-toolbar">
              <p className="ri-panel-meta">
                Templates with a start date in the past generate every due period through today
                (or the end date) as draft invoices when you create them.
              </p>
              <button type="button" className="btn-cyan-primary" onClick={() => { setShowNew(true); setCreatedNote(null); }}>
                New template
              </button>
            </div>
          ) : (
            <form className="ri-form" onSubmit={onCreate}>
              <div className="ri-form-row">
                <label className="ri-field">
                  <span>Template name</span>
                  <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required />
                </label>
                <label className="ri-field">
                  <span>Client</span>
                  <select
                    className="form-input"
                    value={clientId}
                    onChange={(e) => setClientId(Number(e.target.value))}
                    required
                  >
                    <option value={0}>—</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="ri-form-row">
                <label className="ri-field">
                  <span>Frequency</span>
                  <select className="form-input" value={rrule} onChange={(e) => setRrule(e.target.value)}>
                    {CADENCES.map((p) => (
                      <option key={p.rrule} value={p.rrule}>{p.label}</option>
                    ))}
                  </select>
                </label>
                <div />
              </div>
              <div className="ri-form-row">
                <label className="ri-field">
                  <span>Start date</span>
                  <DateInput value={startsAt} onChange={setStartsAt} required />
                </label>
                <label className="ri-field">
                  <span>End date (optional)</span>
                  <DateInput value={endsAt} onChange={setEndsAt} />
                </label>
              </div>
              <div className="ri-items">
                <span className="ri-items-label">Line items</span>
                {items.map((it, idx) => (
                  <div key={idx} className="ri-item-row">
                    <input
                      className="form-input"
                      placeholder="Description"
                      value={it.description}
                      onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, description: e.target.value } : x)))}
                    />
                    <input
                      className="form-input ri-num"
                      placeholder="Qty"
                      value={it.quantity}
                      onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, quantity: e.target.value } : x)))}
                    />
                    <MoneyInput
                      className="ri-num"
                      placeholder="Unit price"
                      value={it.unit_amount}
                      onChange={(next) => setItems(items.map((x, i) => (i === idx ? { ...x, unit_amount: next } : x)))}
                    />
                    {items.length > 1 && (
                      <button
                        type="button"
                        className="ri-link"
                        onClick={() => setItems(items.filter((_, i) => i !== idx))}
                      >
                        remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="ri-link"
                  onClick={() => setItems([...items, { description: '', quantity: '1', unit_amount: '0' }])}
                >
                  + add line
                </button>
              </div>
              <div className="ri-form-row">
                <label className="ri-field">
                  <span>Tax (USD)</span>
                  <MoneyInput value={taxAmount} onChange={setTaxAmount} />
                </label>
              </div>
              <label className="ri-field">
                <span>Notes</span>
                <textarea className="form-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </label>
              <p className="ri-hint">
                Past-due periods between start and today (or end) are created as draft invoices
                immediately. Future periods generate on the daily schedule.
              </p>
              {error && <p className="ri-error" role="alert">{error}</p>}
              <div className="ri-form-actions">
                <button
                  type="button"
                  className="btn-charcoal-secondary"
                  onClick={() => { setShowNew(false); setError(null); }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-cyan-primary" disabled={create.isPending}>
                  {create.isPending ? 'Creating…' : 'Create template'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {list.isPending ? (
        <div className="card"><p className="ri-status">Loading templates…</p></div>
      ) : list.error ? (
        <div className="card"><p className="ri-status ri-error">Failed to load.</p></div>
      ) : rows.length === 0 ? (
        <div className="card">
          <div className="ri-empty">
            <p>No repeating invoice templates yet.</p>
            <p className="ri-empty-meta">
              Create a template with a start date, optional end date, and frequency.
            </p>
          </div>
        </div>
      ) : (
        <div className="card card-tight ri-table-card table-scroll-card">
          <TableScroll id="recurring-invoices">
            <table className="ri-table">
              <thead>
                <tr>
                  <SortableTh column="name" label="Name" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="client" label="Client" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="schedule" label="Frequency" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="next_due" label="Next due" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="subtotal" label="Subtotal" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} numeric className="ri-numeric" />
                  <SortableTh column="status" label="Status" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((t) => {
                  const subtotal = t.line_items.reduce(
                    (sum, li) => sum + Number(li.quantity) * Number(li.unit_amount),
                    0,
                  );
                  return (
                    <tr key={t.id} className={t.is_active ? '' : 'ri-row-inactive'}>
                      <td>{t.name}</td>
                      <td>{t.client_company_name ?? t.client_name ?? `#${t.client_id}`}</td>
                      <td>
                        {labelForRrule(t.rrule)}
                        <div className="ri-schedule-meta">
                          from {fmtDate(t.starts_at)}
                          {t.ends_at ? ` · to ${fmtDate(t.ends_at)}` : ''}
                        </div>
                      </td>
                      <td>{t.next_due_at ? fmtDate(t.next_due_at) : '—'}</td>
                      <td className="ri-numeric">{formatMoney(subtotal.toFixed(2))}</td>
                      <td>
                        {t.is_active
                          ? <span className="ri-pill ri-pill-ok">active</span>
                          : <span className="ri-pill">paused</span>}
                      </td>
                      <td>
                        {can('invoice.write') && (
                          <>
                            <IconAction
                              action={t.is_active ? 'pause' : 'resume'}
                              onClick={() => update.mutate({ id: t.id, is_active: !t.is_active })}
                              disabled={update.isPending}
                              iconSize={16}
                            />
                            <IconAction
                              action="delete"
                              onClick={() => {
                                if (confirm(`Delete template "${t.name}"? Already-generated invoices are kept.`)) {
                                  remove.mutate(t.id);
                                }
                              }}
                              disabled={remove.isPending}
                              iconSize={16}
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        </div>
      )}
    </>
  );
}
